import { PureComponent } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";

import BuildDuration from "~/components/build/Duration";
import BuildMessage from "~/components/build/Message";
import PipelineStateIcon, { VARIANTS } from "~/components/shared/PipelineStateIcon";

import Pipeline from "~/components/build/Header/pipeline";
import BuildNavigator from "~/components/build/Header/BuildNavigator";
import { Branch } from "~/components/build/Header/Branch";
import { Commit } from "~/components/build/Header/Commit";
import { Avatar } from "~/components/build/Header/Avatar";
import { AuthorAndTime } from "~/components/build/Header/AuthorAndTime";
import { MetaInformation } from "~/components/build/Header/MetaInformation";
import { CancelButton } from "~/components/build/Header/CancelButton";
import RetryFailedJobsButton from "~/components/build/Header/RetryFailedJobsButton";
import { RebuildButton } from "~/components/build/Header/RebuildButton";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { Build } from "~/components/build/Show/lib/types";
import { PullRequest } from "~/components/build/Header/PullRequest";

interface BuildHeaderProps {
  currentView?: BuildView;
  setBuildData: (build: Build) => void;
  reloadBuild: () => void;
  build: Build;
  showJobs?: boolean;
  showRebuild?: boolean;
  showProject?: boolean;
  showOrganization?: boolean;
}

interface State {
  showRebuildDialog: boolean;
}

class BuildHeaderComponent extends PureComponent<BuildHeaderProps, State> {
  static defaultProps = {
    showJobs: true,
  };

  state = {
    showRebuildDialog: false,
  };

  componentDidMount() {
    ReactDOM.findDOMNode(this)?.addEventListener("ajax:error", this._onAjaxError);
  }

  componentWillUnmount() {
    ReactDOM.findDOMNode(this)?.removeEventListener("ajax:error", this._onAjaxError);
  }

  _onAjaxError = (event) => {
    event.preventDefault();

    const [response] = event.detail;

    if (response && response.message) {
      alert(response.message);
    }
  };

  buildIsInNeutralState = () => {
    return ["canceling", "canceled", "scheduled"].includes(this.props.build.state);
  };

  bootstrapState = () => {
    switch (this.props.build.state) {
      case "failing":
      case "failed":
        return "danger";
      case "passed":
      case "blocked":
        switch (this.props.build.blockedState) {
          case "failed":
            return "danger";
          case "running":
            return "warning";
          case "passed":
          default:
            return "success";
        }
      case "started":
        return "warning";
      default:
        return "default";
    }
  };

  render() {
    const { build, showJobs } = this.props;
    const bootstrapPanelState = this.bootstrapState();

    const panelClassName = classNames(
      `panel panel-${bootstrapPanelState} build-panel build-state-${build.state}`,
      {
        "mb-2": Features.BuildSidebar && !this.props.showProject,
        "mb-4": this.props.showProject,
        // This is a tad hacky but we need a variant of bootstraps `panel-default` styles
        // for "neutral" build states
        "panel-neutral": this.buildIsInNeutralState(),
      },
    );

    let buildMessage = <BuildMessage message={build.message} />;
    if (build.path !== window.location.pathname) {
      buildMessage = (
        <a href={build.path} className="color-inherit hover-color-inherit">
          {buildMessage}
        </a>
      );
    }

    const isRunning =
      build.state === "failing" ||
      build.state === "started" ||
      build.state === "canceling" ||
      (build.state === "blocked" && build.blockedState === "running");
    const showTopBar = bootstrapPanelState !== "default" || this.buildIsInNeutralState();

    const retryNode = (
      <RetryFailedJobsButton
        build={build}
        setBuildData={this.props.setBuildData}
        className="btn-default"
      />
    );
    const cancelNode = <CancelButton build={build} setBuildData={this.props.setBuildData} />;
    const rebuildNode = this.props.showRebuild ? (
      <RebuildButton build={build} className="btn-default" />
    ) : null;

    return (
      <div className="build-header build-header-tremor">
        {this._projectNode()}
        <div className={panelClassName}>
          {showTopBar ? (
            <div
              className="panel-bar build-speed-hacks"
              style={
                isRunning
                  ? {
                      backgroundImage:
                        "linear-gradient(-45deg, rgba(255,255,255,.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.5) 50%, rgba(255,255,255,.5) 75%, transparent 75%, transparent)",
                      backgroundSize: "12px 12px",
                    }
                  : undefined
              }
            />
          ) : null}
          <div className="panel-heading">
            <div className="flex items-center">
              <div className="flex min-w-0 flex-auto flex-wrap items-center">
                <div
                  className="build-commit mr-2 min-w-0 flex-auto"
                  style={{
                    flexBasis: 300,
                  }}
                >
                  <h3 className="panel-title">{buildMessage}</h3>
                  <div className="mt-1 flex gap-3">
                    <BuildNavigator {...build} currentView={this.props.currentView} />
                    {build.branchName && (
                      <>
                        <span className="text-gray-600">/</span>
                        <Branch {...build} />
                      </>
                    )}

                    {build.commitId && (
                      <>
                        <span className="text-gray-600">/</span>
                        <Commit {...build} />
                      </>
                    )}

                    {build.pullRequest && (
                      <span>
                        (<PullRequest {...build} />)
                      </span>
                    )}
                  </div>
                </div>
                <div className="build-duration mr-3 flex-none">
                  <BuildDuration build={build} />
                </div>
              </div>
              <div className="build-status-icon build-speed-hacks-child flex flex-none items-center justify-center">
                <PipelineStateIcon
                  build={build}
                  style={{ width: "44px", height: "44px" }}
                  variant={VARIANTS.large}
                />
              </div>
            </div>
          </div>

          {showJobs && (
            <Pipeline
              build={build}
              setBuildData={this.props.setBuildData}
              reloadBuild={this.props.reloadBuild}
            />
          )}

          <div className="panel-footer flex flex-wrap items-center px-2 py-0">
            <div className="flex flex-wrap items-stretch overflow-hidden" style={{ flexGrow: 100 }}>
              <div className="-ml-1 flex items-center p-2">
                <Avatar {...build} />
                <AuthorAndTime {...build} />
              </div>

              <div
                className="border-gray flex min-w-0 flex-auto items-center border-t py-1"
                style={{
                  marginTop: -1,
                  marginLeft: -1,
                }}
              >
                <div
                  className={classNames(
                    "border-mid-gray flex min-w-0 flex-auto flex-col border-l px-2",
                    {
                      "max-w-full truncate": build.triggeredFrom && build.triggeredFrom.url,
                    },
                  )}
                >
                  <MetaInformation {...build} />
                </div>
              </div>
            </div>
            {(retryNode || rebuildNode || cancelNode) && (
              <div className="-mx-1 flex space-x-1 p-2">
                {retryNode}
                {rebuildNode}
                {cancelNode}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  _projectNode = () => {
    if (this.props.showProject) {
      return (
        <div style={{ marginBottom: 10 }}>
          <a href={this.props.build.project.url} className="dark-gray">
            {this.props.showOrganization && `${this.props.build.account.name} / `}
            {this.props.build.project.name}
          </a>
        </div>
      );
    }
  };
}

export default BuildHeaderComponent;
